<script setup lang="ts">
import { useWindowScroll } from '@vueuse/core';

import type { Link } from '@/types/common';
import { getSpaId, isAuthenticated } from '@/utils/auth';
import { computed } from 'vue';

const spaId = getSpaId();

const menu: Link[] = [
  { name: 'Найти реплей', link: '/find-replay' },
  { name: 'Конкурсы', link: '/contest' },
];

const rightPanelPure: Array<Link & { class?: string; withDogtag?: boolean; onlyAuth?: boolean }> = [
  {
    name: 'Загрузить реплей',
    link: '/replay/upload',
    class: 'button secondary upload',
  },
  {
    name: 'Мои реплеи',
    link: `/account/${spaId}`,
    class: 'account',
    withDogtag: true,
    onlyAuth: true,
  },
];
const rightPanelRendered = computed(() =>
  rightPanelPure.filter((i) => {
    return isAuthenticated() ? i : !i.onlyAuth || false;
  })
);

const { y: scrollPosY } = useWindowScroll();

const classes = computed(() => ({
  'scrolled blur-l op-100': scrollPosY.value > 20,
}));
</script>

<template>
  <div class="offset"></div>
  <header :class="classes">
    <div class="container">
      <nav class="flex-between text-sm uppercase">
        <ul class="menu">
          <li class="logo">
            <nuxt-link to="/">
              <img
                src="/logo.svg"
                alt="Логотип Реплеи | Мир Кораблей"
                loading="eager"
              />
            </nuxt-link>
          </li>
          <li
            v-for="(item, index) in menu"
            :key="index"
          >
            <nuxt-link :to="item.link">
              {{ item.name }}
            </nuxt-link>
          </li>
        </ul>

        <ul class="right-panel">
          <li
            v-for="(linkItem, index) in rightPanelRendered"
            :key="index"
          >
            <nuxt-link
              :class="linkItem.class"
              :to="linkItem.link"
            >
              <lazy-dogtag
                v-if="linkItem.withDogtag && spaId"
                :class="linkItem.class"
                :spa-id="spaId"
                size="sm"
              />
              {{ linkItem.name }}
            </nuxt-link>
          </li>
        </ul>
      </nav>
    </div>
  </header>
</template>

<style scoped lang="postcss">
.offset {
  height: 115px;
}

.op {
  &-0 {
    opacity: 0;
  }

  &-100 {
    opacity: 1;
  }
}

header {
  margin-top: var(--common-menu-height);
  margin-bottom: var(--space-m-var_01);
  z-index: 3;
  transition: opacity 200ms ease-in-out;
  position: fixed;
  width: 100dvw;

  &.scrolled {
    top: 0;
    background-color: var(--color-deep-blue-85);
  }

  nav {
    flex: 1 1;

    color: var(--color-white-100);

    ul {
      display: inline-flex;
      align-items: center;
      line-height: 1;

      &.menu {
        li {
          &.logo {
            margin-right: var(--space-m-var_01);

            a {
              padding: 15px 0 6px 0;

              img {
                width: 123px;
              }
            }
          }

          &:not(.logo) {
            a {
              transition:
                border-color 200ms ease-in-out,
                background-color 200ms ease-in-out;
              border-top: 4px solid transparent;
              border-radius: var(--border-radius-default);

              &:hover {
                background: radial-gradient(
                  51.19% 76.79% at 50% 0%,
                  rgb(0 119 254 / 30%) 0%,
                  rgb(0 119 254 / 0) 100%
                );

                border-color: var(--color-accient-sea-blue-normal);
              }
            }
          }
        }

        a {
          padding: 22px 20px 18px;
          display: block;

          &.button {
            padding: var(--space-s-var_02) var(--space-m-var_02);
          }

          &:has(.dogtag) {
            display: flex;
            align-items: center;
            gap: var(--space-s-var_02);
            padding: 0 var(--space-s-var_03) 0 0;

            margin-left: var(--space-s-var_03);
          }
        }
      }

      &.right-panel {
        gap: var(--space-s-var_03);

        .account {
          display: inline-block;
          vertical-align: middle;
        }

        .upload {
          --space: var(--space-m-var_02);
          padding-left: var(--space);
          padding-right: var(--space);
        }
      }
    }
  }
}
</style>
